import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { coopPlatformService as svc } from '../../services/coopPlatformService';
import { formatDate } from '../../utils/helpers';
import LoadingSpinner from '../../components/common/LoadingSpinner';

// Cooperative societies are white-label tenants hosted at {slug}.mypcredit.com. PCredit creates and
// manages them here; each society's members and money live only on the Coop platform.

const STATUS_BADGE = {
  draft: 'badge-gray', pending_approval: 'badge-warning', provisioning: 'badge-info',
  failed: 'badge-danger', active: 'badge-success', suspended: 'badge-danger',
};
const OPERATING_MODELS = [
  { value: 'thrift_credit', label: 'Thrift and credit' },
  { value: 'employee', label: 'Employee cooperative (payroll deductions)' },
  { value: 'community', label: 'Community cooperative' },
  { value: 'multi_branch', label: 'Multi-branch cooperative' },
];
const human = (s) => (s || '').replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
const errorMessage = (e) => e?.response?.data?.message || 'Something went wrong. Please try again.';
const fieldErrors = (e) => e?.response?.data?.errors || {};

// Each society's own brand colours (from its branding). Only plain #rrggbb values are used in styles.
const HEX = /^#[0-9a-f]{6}$/i;
const brand = (s) => ({
  primary: HEX.test(s.branding?.primary_color || '') ? s.branding.primary_color : '#64748b',
  accent: HEX.test(s.branding?.accent_color || '') ? s.branding.accent_color : null,
});
const hue = (hex) => {
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255);
  const max = Math.max(r, g, b), min = Math.min(r, g, b), d = max - min;
  const saturation = d === 0 ? 0 : d / (1 - Math.abs(max + min - 1));
  if (saturation < 0.2) return 400 + max; // greys (and the fallback slate) sort after the colours, darkest first
  const h = max === r ? ((g - b) / d) % 6 : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  return ((h * 60) + 360) % 360;
};
const SORTS = {
  newest: { label: 'Newest first', fn: (a, b) => new Date(b.created_at) - new Date(a.created_at) },
  name: { label: 'Name', fn: (a, b) => a.name.localeCompare(b.name) },
  colour: { label: 'Primary colour', fn: (a, b) => hue(brand(a).primary) - hue(brand(b).primary) },
};

function BrandTile({ society }) {
  const { primary, accent } = brand(society);
  return (
    <span
      aria-hidden
      title={`Primary ${primary}${accent ? ` · accent ${accent}` : ''}`}
      style={{
        display: 'inline-grid', placeItems: 'center', width: 36, height: 36, flex: '0 0 36px', borderRadius: 10,
        background: `linear-gradient(135deg, ${primary}, ${primary}cc)`, color: '#fff', fontWeight: 700,
        fontFamily: "'Space Grotesk', 'Inter', sans-serif", boxShadow: `0 0 0 2px ${accent || primary}55, 0 6px 16px -8px ${primary}`,
      }}
    >
      {(society.branding?.short_name || society.name || '?').trim().charAt(0).toUpperCase()}
    </span>
  );
}

function ColourChip({ society }) {
  const { primary, accent } = brand(society);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'monospace', fontSize: '0.8rem', color: 'var(--text-2)' }}>
      <span style={{ width: 12, height: 12, borderRadius: 4, background: primary, border: '1px solid var(--border-bright)' }} />
      {primary.toUpperCase()}
      {accent && <span title={`Accent ${accent}`} style={{ width: 8, height: 8, borderRadius: 999, background: accent }} />}
    </span>
  );
}

function StatusBadge({ status }) {
  return <span className={`badge ${STATUS_BADGE[status] || 'badge-gray'}`}>{human(status)}</span>;
}

// ── List ─────────────────────────────────────────────────────────────────────

export function CoopSocietiesList() {
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('newest');
  const { data = [], isLoading, error, refetch } = useQuery({
    queryKey: ['coop-societies', status, q],
    queryFn: () => svc.list({ status: status || undefined, q: q || undefined }).then((r) => r.data.data),
  });

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '1rem', flexWrap: 'wrap' }}>
        <div>
          <h1>Cooperative Societies</h1>
          <p>White-label cooperatives running on their own subdomain. Creating, suspending and reactivating a society needs a second approver.</p>
        </div>
        <Link to="/admin/coop-societies/new" className="btn btn-primary">+ New society</Link>
      </div>

      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <input className="form-input" style={{ maxWidth: 260 }} placeholder="Search name or subdomain…" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className="form-input" style={{ maxWidth: 220 }} value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Filter by status">
          <option value="">All statuses</option>
          {Object.keys(STATUS_BADGE).map((s) => <option key={s} value={s}>{human(s)}</option>)}
        </select>
        <select className="form-input" style={{ maxWidth: 220 }} value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Sort societies">
          {Object.entries(SORTS).map(([k, v]) => <option key={k} value={k}>Sort: {v.label}</option>)}
        </select>
      </div>

      <div className="card">
        {isLoading ? <LoadingSpinner /> : error ? (
          <div className="alert alert-error"><span>⚠</span><span>{errorMessage(error)}</span>
            <button className="btn btn-ghost" onClick={() => refetch()} style={{ marginLeft: 'auto' }}>Retry</button></div>
        ) : data.length === 0 ? (
          <p style={{ color: 'var(--text-2)', textAlign: 'center', padding: '2rem 0' }}>No societies yet.</p>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead><tr><th>Society</th><th>Colour</th><th>Web address</th><th>Model</th><th>Status</th><th>Created</th></tr></thead>
              <tbody>
                {[...data].sort(SORTS[sort].fn).map((s) => (
                  <tr key={s.id} style={{ boxShadow: `inset 3px 0 0 ${brand(s).primary}` }}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <BrandTile society={s} />
                        <div>
                          <Link to={`/admin/coop-societies/${s.id}`} style={{ fontWeight: 600 }}>{s.name}</Link>
                          {s.pending_action && <div><span className="badge badge-warning">{human(s.pending_action)} awaiting approval</span></div>}
                        </div>
                      </div>
                    </td>
                    <td><ColourChip society={s} /></td>
                    <td><a href={s.url} target="_blank" rel="noreferrer">{s.host}</a></td>
                    <td>{human(s.operating_model)}</td>
                    <td><StatusBadge status={s.status} /></td>
                    <td>{formatDate(s.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Create ───────────────────────────────────────────────────────────────────

const EMPTY = {
  name: '', slug: '', operating_model: 'thrift_credit', plan: '', registration_no: '', state: '', address: '',
  fiscal_year_start_month: 1, admin_first_name: '', admin_last_name: '', admin_email: '',
};
const toSlug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 30);

export function CoopSocietyNew() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [slugTouched, setSlugTouched] = useState(false);
  const [slugCheck, setSlugCheck] = useState(null);
  const { data: plans = [] } = useQuery({ queryKey: ['coop-plans'], queryFn: () => svc.plans().then((r) => r.data.data) });

  useEffect(() => {
    if (!form.slug) { setSlugCheck(null); return undefined; }
    const t = setTimeout(() => svc.checkSlug(form.slug).then((r) => setSlugCheck(r.data.data)).catch(() => setSlugCheck(null)), 350);
    return () => clearTimeout(t);
  }, [form.slug]);

  const create = useMutation({
    mutationFn: () => svc.create({ ...form, plan: form.plan || null, fiscal_year_start_month: Number(form.fiscal_year_start_month) }),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ['coop-societies'] }); navigate(`/admin/coop-societies/${r.data.data.id}`); },
  });
  const errs = fieldErrors(create.error);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value, ...(k === 'name' && !slugTouched ? { slug: toSlug(e.target.value) } : {}) }));
  const field = (k, label, props = {}) => (
    <div className="form-group">
      <label className="form-label" htmlFor={`coop-${k}`}>{label}</label>
      <input id={`coop-${k}`} className="form-input" value={form[k]} onChange={set(k)} {...props} />
      {errs[k] && <p style={{ color: '#ff7e8a', fontSize: '0.8rem', marginTop: 4 }}>{errs[k][0]}</p>}
    </div>
  );

  return (
    <div style={{ maxWidth: 760 }}>
      <div className="page-header">
        <h1>New cooperative society</h1>
        <p>This creates a draft. After you submit it, a different administrator must approve it before the society is set up.</p>
      </div>
      {create.error && <div className="alert alert-error"><span>⚠</span><span>{errorMessage(create.error)}</span></div>}
      <form className="card" onSubmit={(e) => { e.preventDefault(); create.mutate(); }}>
        <h3 style={{ marginBottom: '1rem' }}>Society</h3>
        {field('name', 'Society name', { required: true })}
        <div className="form-group">
          <label className="form-label" htmlFor="coop-slug">Web address</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input id="coop-slug" className="form-input" value={form.slug} required
              onChange={(e) => { setSlugTouched(true); setForm((f) => ({ ...f, slug: toSlug(e.target.value) })); }} />
            <span style={{ color: 'var(--text-2)', whiteSpace: 'nowrap' }}>.mypcredit.com</span>
          </div>
          {slugCheck && (
            <p style={{ fontSize: '0.8rem', marginTop: 4, color: slugCheck.available ? '#00d9a5' : '#ff7e8a' }}>
              {slugCheck.available ? `✓ ${slugCheck.host} is available` : slugCheck.reason}
            </p>
          )}
          {errs.slug && <p style={{ color: '#ff7e8a', fontSize: '0.8rem', marginTop: 4 }}>{errs.slug[0]}</p>}
          <p style={{ fontSize: '0.75rem', color: 'var(--text-3)', marginTop: 4 }}>Cannot be changed after approval.</p>
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="coop-model">Operating model</label>
          <select id="coop-model" className="form-input" value={form.operating_model} onChange={set('operating_model')}>
            {OPERATING_MODELS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
          <p style={{ fontSize: '0.75rem', color: 'var(--text-3)', marginTop: 4 }}>Sets the starting policies; the society can change them later.</p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0 1rem' }}>
          <div className="form-group">
            <label className="form-label" htmlFor="coop-plan">Plan</label>
            <select id="coop-plan" className="form-input" value={form.plan} onChange={set('plan')}>
              <option value="">No plan</option>
              {plans.map((p) => <option key={p.code} value={p.code}>{p.name}</option>)}
            </select>
          </div>
          {field('registration_no', 'Registration number')}
          {field('state', 'State')}
          <div className="form-group">
            <label className="form-label" htmlFor="coop-fy">Financial year starts</label>
            <select id="coop-fy" className="form-input" value={form.fiscal_year_start_month} onChange={set('fiscal_year_start_month')}>
              {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
          </div>
        </div>
        {field('address', 'Address')}

        <h3 style={{ margin: '1.5rem 0 0.5rem' }}>First administrator</h3>
        <p style={{ color: 'var(--text-2)', fontSize: '0.85rem', marginBottom: '1rem' }}>They'll receive an email invite (valid 72 hours) once the society is set up, and will add the rest of their staff.</p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1rem' }}>
          {field('admin_first_name', 'First name', { required: true })}
          {field('admin_last_name', 'Last name', { required: true })}
        </div>
        {field('admin_email', 'Email', { type: 'email', required: true })}

        <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
          <button className="btn btn-primary" type="submit" disabled={create.isPending || slugCheck?.available === false}>
            {create.isPending ? 'Saving…' : 'Save draft'}
          </button>
          <Link to="/admin/coop-societies" className="btn btn-ghost">Cancel</Link>
        </div>
      </form>
    </div>
  );
}

// ── Detail ───────────────────────────────────────────────────────────────────

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function Branding({ society }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState(null);
  const b = draft || society.branding;
  const save = useMutation({
    mutationFn: () => svc.updateBranding(society.id, {
      display_name: b.display_name, short_name: b.short_name, primary_color: b.primary_color, accent_color: b.accent_color,
      email_sender_name: b.email_sender_name, reply_to_email: b.reply_to_email, support_email: b.support_email,
      support_phone: b.support_phone, footer_text: b.footer_text,
    }),
    onSuccess: () => { setDraft(null); qc.invalidateQueries({ queryKey: ['coop-society', society.id] }); },
  });
  const logo = useMutation({
    mutationFn: async (file) => svc.uploadLogo(society.id, file.name, await readFileAsBase64(file)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['coop-society', society.id] }),
  });
  const set = (k) => (e) => setDraft({ ...b, [k]: e.target.value });
  const errs = fieldErrors(save.error);
  const text = (k, label, type = 'text') => (
    <div className="form-group">
      <label className="form-label" htmlFor={`brand-${k}`}>{label}</label>
      <input id={`brand-${k}`} type={type} className="form-input" value={b[k] || ''} onChange={set(k)} />
      {errs[k] && <p style={{ color: '#ff7e8a', fontSize: '0.8rem', marginTop: 4 }}>{errs[k][0]}</p>}
    </div>
  );

  return (
    <div className="card">
      <h3 style={{ marginBottom: '1rem' }}>Branding</h3>
      {save.error && <div className="alert alert-error"><span>⚠</span><span>{errorMessage(save.error)}</span></div>}
      {logo.error && <div className="alert alert-error"><span>⚠</span><span>{errorMessage(logo.error)}</span></div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0 1rem' }}>
        {text('display_name', 'Display name')}
        {text('short_name', 'Short name')}
        <div className="form-group">
          <label className="form-label" htmlFor="brand-primary">Primary colour</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input type="color" aria-label="Pick primary colour" value={b.primary_color} onChange={set('primary_color')} />
            <input id="brand-primary" className="form-input" value={b.primary_color} onChange={set('primary_color')} />
          </div>
          {errs.primary_color && <p style={{ color: '#ff7e8a', fontSize: '0.8rem', marginTop: 4 }}>{errs.primary_color[0]}</p>}
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="brand-accent">Accent colour</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input type="color" aria-label="Pick accent colour" value={b.accent_color} onChange={set('accent_color')} />
            <input id="brand-accent" className="form-input" value={b.accent_color} onChange={set('accent_color')} />
          </div>
        </div>
        {text('email_sender_name', 'Email sender name')}
        {text('reply_to_email', 'Reply-to email', 'email')}
        {text('support_email', 'Support email', 'email')}
        {text('support_phone', 'Support phone')}
      </div>
      {text('footer_text', 'Footer text')}
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
        <button className="btn btn-primary" disabled={!draft || save.isPending} onClick={() => save.mutate()}>{save.isPending ? 'Saving…' : 'Save branding'}</button>
        <label className="btn btn-ghost" style={{ cursor: 'pointer' }}>
          {logo.isPending ? 'Uploading…' : society.branding.logo_url ? 'Replace logo' : 'Upload logo'}
          <input type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={(e) => e.target.files[0] && logo.mutate(e.target.files[0])} />
        </label>
        <span style={{ fontSize: '0.75rem', color: 'var(--text-3)' }}>PNG, JPEG or WebP, up to 512 KB.</span>
      </div>
    </div>
  );
}

export function CoopSocietyDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const [reason, setReason] = useState('');
  const [notice, setNotice] = useState('');
  const { data: s, isLoading, error } = useQuery({
    queryKey: ['coop-society', id],
    queryFn: () => svc.get(id).then((r) => r.data.data),
    refetchInterval: (query) => (query.state.data?.status === 'provisioning' ? 3000 : false),
  });
  const { data: events = [] } = useQuery({ queryKey: ['coop-society-audit', id], queryFn: () => svc.auditEvents(id).then((r) => r.data.data) });
  const act = useMutation({
    mutationFn: ({ action, data }) => svc.action(id, action, data),
    onSuccess: (r, { done }) => {
      qc.setQueryData(['coop-society', id], r.data.data);
      qc.invalidateQueries({ queryKey: ['coop-societies'] });
      qc.invalidateQueries({ queryKey: ['coop-society-audit', id] });
      setReason('');
      setNotice(done || '');
    },
  });

  if (isLoading) return <LoadingSpinner />;
  if (error) return <div className="alert alert-error"><span>⚠</span><span>{errorMessage(error)}</span></div>;

  const job = s.latest_provisioning;
  const run = (action, data, done) => { setNotice(''); act.mutate({ action, data, done }); };

  return (
    <div style={{ display: 'grid', gap: '1.25rem' }}>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '1rem', flexWrap: 'wrap', marginBottom: 0 }}>
        <div>
          <p style={{ marginBottom: 4 }}><Link to="/admin/coop-societies">← Cooperative Societies</Link></p>
          <h1>{s.name}</h1>
          <p><a href={s.url} target="_blank" rel="noreferrer">{s.host}</a> · {human(s.operating_model)}</p>
        </div>
        <StatusBadge status={s.status} />
      </div>

      {notice && <div className="alert alert-success"><span>✓</span><span>{notice}</span></div>}
      {act.error && <div className="alert alert-error"><span>⚠</span><span>{errorMessage(act.error)}</span></div>}

      <div className="card">
        <h3 style={{ marginBottom: '0.75rem' }}>Next step</h3>
        {s.status === 'draft' && (
          <>
            {s.rejection_reason && <div className="alert alert-warning"><span>↩</span><span>Returned: {s.rejection_reason}</span></div>}
            <p style={{ color: 'var(--text-2)', marginBottom: '0.75rem' }}>Check the details below, then submit for approval.</p>
            <button className="btn btn-primary" disabled={act.isPending} onClick={() => run('submit', {}, 'Submitted. A different administrator must now approve it.')}>Submit for approval</button>
          </>
        )}
        {s.status === 'pending_approval' && (
          <>
            <p style={{ color: 'var(--text-2)', marginBottom: '0.75rem' }}>Submitted by {s.submitted_by?.name || s.submitted_by?.email}. Approving sets up the society and emails its first administrator.</p>
            <textarea className="form-input" rows={2} placeholder="Reason (required to send back)" value={reason} onChange={(e) => setReason(e.target.value)} style={{ marginBottom: '0.75rem' }} />
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button className="btn btn-primary" disabled={act.isPending} onClick={() => run('approve', {}, 'Approved. The society is being set up.')}>Approve and set up</button>
              <button className="btn btn-ghost" disabled={act.isPending || !reason.trim()} onClick={() => run('reject', { reason }, 'Sent back to draft.')}>Send back</button>
            </div>
          </>
        )}
        {s.status === 'provisioning' && <p>Setting up the society… this page refreshes automatically.</p>}
        {s.status === 'failed' && (
          <>
            <div className="alert alert-error"><span>⚠</span><span>Set-up failed: {job?.error}</span></div>
            <button className="btn btn-primary" disabled={act.isPending} onClick={() => run('retry-provisioning', {}, 'Retrying set-up.')}>Retry set-up</button>
          </>
        )}
        {['active', 'suspended'].includes(s.status) && !s.pending_action && (
          <>
            <textarea className="form-input" rows={2} placeholder="Reason" value={reason} onChange={(e) => setReason(e.target.value)} style={{ marginBottom: '0.75rem' }} />
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              {s.status === 'active' ? (
                <button className="btn btn-ghost" style={{ color: '#ff7e8a' }} disabled={act.isPending || !reason.trim()} onClick={() => run('status-change', { action: 'suspend', reason }, 'Suspension requested. A different administrator must approve it.')}>Request suspension</button>
              ) : (
                <button className="btn btn-primary" disabled={act.isPending || !reason.trim()} onClick={() => run('status-change', { action: 'reactivate', reason }, 'Reactivation requested. A different administrator must approve it.')}>Request reactivation</button>
              )}
              {s.status === 'active' && <button className="btn btn-ghost" disabled={act.isPending} onClick={() => run('admin-invite', {}, `Invite re-sent to ${s.admin_email}.`)}>Re-send admin invite</button>}
            </div>
          </>
        )}
        {s.pending_action && (
          <>
            <p style={{ marginBottom: '0.75rem' }}><strong>{human(s.pending_action)}</strong> requested by {s.pending_action_by?.name || s.pending_action_by?.email}: “{s.pending_action_reason}”</p>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button className="btn btn-primary" disabled={act.isPending} onClick={() => run('status-change-decision', { approve: true }, 'Change approved.')}>Approve</button>
              <button className="btn btn-ghost" disabled={act.isPending} onClick={() => run('status-change-decision', { approve: false }, 'Change declined.')}>Decline</button>
            </div>
          </>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.25rem' }}>
        <div className="card">
          <h3 style={{ marginBottom: '0.75rem' }}>Details</h3>
          <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.4rem 1rem', fontSize: '0.9rem' }}>
            <dt style={{ color: 'var(--text-2)' }}>Plan</dt><dd>{s.plan || '—'}</dd>
            <dt style={{ color: 'var(--text-2)' }}>Registration no.</dt><dd>{s.registration_no || '—'}</dd>
            <dt style={{ color: 'var(--text-2)' }}>State</dt><dd>{s.state || '—'}</dd>
            <dt style={{ color: 'var(--text-2)' }}>First admin</dt><dd>{s.admin_first_name} {s.admin_last_name} ({s.admin_email})</dd>
            <dt style={{ color: 'var(--text-2)' }}>Created by</dt><dd>{s.created_by?.name || s.created_by?.email} · {formatDate(s.created_at)}</dd>
            {s.approved_by?.email && <><dt style={{ color: 'var(--text-2)' }}>Approved by</dt><dd>{s.approved_by.name || s.approved_by.email}</dd></>}
            {s.activated_at && <><dt style={{ color: 'var(--text-2)' }}>Live since</dt><dd>{formatDate(s.activated_at)}</dd></>}
          </dl>
        </div>
        {job && (
          <div className="card">
            <h3 style={{ marginBottom: '0.75rem' }}>Set-up log (attempt {job.attempt})</h3>
            <ul style={{ listStyle: 'none', padding: 0, fontSize: '0.9rem' }}>
              {job.steps.map((st) => (
                <li key={st.name} style={{ padding: '0.25rem 0' }}>
                  {st.status === 'done' ? '✓' : '✗'} {human(st.name)} {st.error && <span style={{ color: '#ff7e8a' }}>— {st.error}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {s.branding && <Branding society={s} />}

      <div className="card">
        <h3 style={{ marginBottom: '0.75rem' }}>Platform activity</h3>
        {events.length === 0 ? <p style={{ color: 'var(--text-2)' }}>No activity yet.</p> : (
          <div className="table-wrapper">
            <table>
              <thead><tr><th>When</th><th>Who</th><th>Action</th></tr></thead>
              <tbody>
                {events.map((e) => (
                  <tr key={e.id}><td>{formatDate(e.at)}</td><td>{e.actor?.name || e.actor?.email || 'System'}</td><td>{human(e.action.replace('society.', ''))}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
